import { useEffect, useState, type ChangeEvent } from 'react';
import { ImagePreviewGrid } from '../components/media/ImagePreviewGrid';
import { uploadImageToCos, resolveImagePreviewUrl } from '../lib/upload/cos';
import {
  ALL_SUPPORT_FEEDBACK_STATUSES,
  SUPPORT_FEEDBACK_CATEGORY_LABELS,
  SUPPORT_FEEDBACK_STATUS_LABELS,
  useSupportFeedbackAdmin,
} from '../features/support';
import type {
  SupportFeedbackCategory,
  SupportFeedbackStatus,
} from '../features/support';

const STATUS_STYLES: Record<SupportFeedbackStatus, string> = {
  pending: 'bg-amber-50 text-amber-700',
  processing: 'bg-sky-50 text-sky-700',
  resolved: 'bg-green-50 text-green-700',
};

const MAX_REOPEN_IMAGES = 3;

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function SupportFeedbackPage() {
  const {
    items,
    total,
    page,
    totalPages,
    userId,
    status,
    selectedId,
    selectedItem,
    loading,
    detailLoading,
    error,
    submitting,
    setPage,
    setUserId,
    setStatus,
    setSelectedId,
    resetFilters,
    reload,
    updateStatus,
    reopenWithComment,
  } = useSupportFeedbackAdmin();
  const [reopenContent, setReopenContent] = useState('');
  const [reopenImages, setReopenImages] = useState<string[]>([]);
  const [reopenImagePreviews, setReopenImagePreviews] = useState<Record<string, string>>({});
  const [reopenError, setReopenError] = useState<string | null>(null);
  const [reopenUploading, setReopenUploading] = useState(false);

  useEffect(() => {
    setReopenContent('');
    setReopenImages([]);
    setReopenImagePreviews({});
    setReopenError(null);
  }, [selectedId]);

  const handleReopenImagesChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    const remaining = MAX_REOPEN_IMAGES - reopenImages.length;
    if (remaining <= 0) {
      setReopenError(`最多上传 ${MAX_REOPEN_IMAGES} 张图片`);
      return;
    }

    try {
      const selected = files.slice(0, remaining);
      setReopenUploading(true);
      for (const file of selected) {
        if (!file.type.startsWith('image/')) {
          setReopenError('只能上传图片文件');
          return;
        }
      }
      const uploaded = await Promise.all(selected.map((file) => uploadImageToCos(file, 'support-comment')));
      if (files.length > remaining) {
        setReopenError(`最多上传 ${MAX_REOPEN_IMAGES} 张图片，已自动保留前 ${remaining} 张`);
      } else {
        setReopenError(null);
      }
      setReopenImages((prev) => [...prev, ...uploaded.map((item) => item.relativePath)]);
      setReopenImagePreviews((prev) => {
        const next = { ...prev };
        for (const item of uploaded) {
          next[item.relativePath] = item.readUrl;
        }
        return next;
      });
    } catch {
      setReopenError('图片上传失败，请重新选择');
    } finally {
      setReopenUploading(false);
    }
  };

  const handleReopenSubmit = async () => {
    const content = reopenContent.trim();
    if (!content) {
      setReopenError('请填写复测说明');
      return;
    }
    const ok = await reopenWithComment(content, reopenImages);
    if (ok) {
      setReopenContent('');
      setReopenImages([]);
      setReopenImagePreviews({});
      setReopenError(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">客服反馈</h1>
          <p className="mt-1 text-sm text-gray-500">
            查看客户端提交的问题截图与文字内容，并标记处理状态。
          </p>
        </div>
        <button
          onClick={() => {
            void reload();
          }}
          className="h-9 px-3 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition-colors"
        >
          刷新
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={userId}
          onChange={(event) => setUserId(event.target.value.replace(/\D/g, ''))}
          placeholder="用户 ID"
          className="h-9 w-32 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <select
          value={status}
          onChange={(event) => setStatus(event.target.value as '' | SupportFeedbackStatus)}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {ALL_SUPPORT_FEEDBACK_STATUSES.map((value) => (
            <option key={value || 'all'} value={value}>
              {value ? SUPPORT_FEEDBACK_STATUS_LABELS[value] : '全部状态'}
            </option>
          ))}
        </select>
        <button
          onClick={resetFilters}
          className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          重置
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_420px] items-start">
        <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">反馈列表</p>
              <p className="mt-1 text-xs text-gray-500">共 {total} 条，按提交时间倒序展示。</p>
            </div>
          </div>

          {error && (
            <div className="px-5 py-6 text-sm text-red-500 border-b border-gray-100">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">当前筛选下暂无反馈</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">用户</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">问题内容</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">图片</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">提交时间</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const active = item.id === selectedId;
                    const categoryLabel =
                      SUPPORT_FEEDBACK_CATEGORY_LABELS[item.category as SupportFeedbackCategory] ?? item.category;
                    const statusLabel =
                      SUPPORT_FEEDBACK_STATUS_LABELS[item.status as SupportFeedbackStatus] ?? item.status;

                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-gray-200 cursor-pointer transition-colors ${
                          active ? 'bg-primary/5' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => setSelectedId(item.id)}
                      >
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-gray-900">{item.nickname || '-'}</div>
                          <div className="mt-1 text-xs text-gray-400">#{item.user_id} {item.email || ''}</div>
                        </td>
                        <td className="px-4 py-3 align-top text-center">
                          <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                            {categoryLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top text-gray-600 max-w-[320px]">
                          <div className="line-clamp-2 whitespace-pre-wrap break-words">{item.content}</div>
                        </td>
                        <td className="px-4 py-3 align-top text-center text-xs text-gray-500">
                          {item.image_count} 张
                        </td>
                        <td className="px-4 py-3 align-top text-center">
                          <span
                            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                              STATUS_STYLES[item.status as SupportFeedbackStatus] ?? 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top text-center text-xs text-gray-500 whitespace-nowrap">
                          {formatDateTime(item.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between px-5 py-4 text-sm text-gray-500 border-t border-gray-100">
            <span>共 {total} 条</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <span className="tabular-nums">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </section>

        <aside className="bg-white border border-gray-200 rounded-xl p-5">
          {detailLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          ) : !selectedItem ? (
            <div className="py-16 text-center text-sm text-gray-400">选择一条反馈查看详情</div>
          ) : (
            <div className="space-y-5">
              <div>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">
                      {selectedItem.nickname || '-'}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      #{selectedItem.user_id} {selectedItem.email || ''}
                    </p>
                  </div>
                  <span
                    className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${
                      STATUS_STYLES[selectedItem.status as SupportFeedbackStatus] ?? 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {SUPPORT_FEEDBACK_STATUS_LABELS[selectedItem.status as SupportFeedbackStatus] ?? selectedItem.status}
                  </span>
                </div>
                <div className="mt-3 text-xs text-gray-400">
                  提交于 {formatDateTime(selectedItem.created_at)}，最近更新 {formatDateTime(selectedItem.updated_at)}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">问题类型</p>
                <p className="mt-2 text-sm text-gray-900">
                  {SUPPORT_FEEDBACK_CATEGORY_LABELS[selectedItem.category as SupportFeedbackCategory] ?? selectedItem.category}
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">处理状态</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedItem.status === 'pending' && (
                    <span className="px-3 py-2 rounded-md text-sm bg-primary text-white">
                      {SUPPORT_FEEDBACK_STATUS_LABELS.pending}
                    </span>
                  )}
                  {(['processing', 'resolved'] as SupportFeedbackStatus[]).map((nextStatus) => {
                    const active = selectedItem.status === nextStatus;
                    return (
                      <button
                        key={nextStatus}
                        onClick={() => {
                          void updateStatus(nextStatus);
                        }}
                        disabled={submitting || active}
                        className={`px-3 py-2 rounded-md text-sm transition-colors ${
                          active
                            ? 'bg-primary text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        } disabled:opacity-50`}
                      >
                        {SUPPORT_FEEDBACK_STATUS_LABELS[nextStatus]}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  复测后仍有问题时，请在下方追加说明并恢复为待处理。
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">问题描述</p>
                <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700 whitespace-pre-wrap break-words">
                  {selectedItem.content}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">截图</p>
                {selectedItem.image_data_urls && selectedItem.image_data_urls.length > 0 ? (
                  <ImagePreviewGrid
                    images={selectedItem.image_data_urls}
                    altPrefix="反馈截图"
                    gridClassName="mt-3 grid grid-cols-2 gap-3"
                    thumbClassName="h-36"
                  />
                ) : (
                  <p className="mt-2 text-sm text-gray-400">用户未上传图片</p>
                )}
              </div>

              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">复测记录</p>
                {selectedItem.comments && selectedItem.comments.length > 0 ? (
                  <div className="mt-3 space-y-3">
                    {selectedItem.comments.map((comment) => (
                      <div key={comment.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                          <span>
                            {comment.admin_nickname || comment.admin_email || `管理员 #${comment.admin_id}`}
                          </span>
                          <span>{formatDateTime(comment.created_at)}</span>
                        </div>
                        <div className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">
                          {comment.content}
                        </div>
                        {comment.image_data_urls && comment.image_data_urls.length > 0 && (
                          <ImagePreviewGrid
                            images={comment.image_data_urls}
                            altPrefix="复测图片"
                            gridClassName="mt-3 grid grid-cols-3 gap-2"
                            thumbClassName="h-24"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-400">暂无复测追加记录</p>
                )}
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                <p className="text-sm font-medium text-gray-900">追加说明并恢复待处理</p>
                <p className="mt-1 text-xs text-gray-500">
                  管理员复测后确认问题仍存在时使用；文字描述必填，图片可选。
                </p>
                <textarea
                  value={reopenContent}
                  onChange={(event) => {
                    setReopenContent(event.target.value);
                    if (reopenError === '请填写复测说明') setReopenError(null);
                  }}
                  rows={4}
                  placeholder="请描述复测步骤、仍存在的问题、期望结果等"
                  className="mt-3 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
                <div className="mt-3 flex items-center gap-3">
                  <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:bg-gray-50">
                    上传图片
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(event) => {
                        void handleReopenImagesChange(event);
                      }}
                    />
                  </label>
                  <span className="text-xs text-gray-500">
                    已选 {reopenImages.length} / {MAX_REOPEN_IMAGES} 张
                  </span>
                </div>
                {reopenImages.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {reopenImages.map((src, index) => (
                      <div key={`${src.slice(0, 32)}-${index}`} className="relative">
                        <ImagePreviewGrid
                          images={[resolveImagePreviewUrl(src, reopenImagePreviews)]}
                          altPrefix="待提交图片"
                          gridClassName="grid"
                          thumbClassName="h-24"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const target = reopenImages[index];
                            setReopenImages((prev) => prev.filter((_, i) => i !== index));
                            setReopenImagePreviews((prev) => {
                              const next = { ...prev };
                              delete next[target];
                              return next;
                            });
                          }}
                          className="absolute right-1 top-1 rounded bg-white/90 px-1.5 py-0.5 text-xs text-red-600 shadow"
                        >
                          删除
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {reopenError && <p className="mt-2 text-xs text-red-500">{reopenError}</p>}
                {reopenUploading && <p className="mt-2 text-xs text-gray-400">图片上传中...</p>}
                <button
                  type="button"
                  onClick={() => {
                    void handleReopenSubmit();
                  }}
                  disabled={submitting || reopenUploading || !reopenContent.trim()}
                  className="mt-3 h-9 rounded-md bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                  {submitting ? '提交中' : '追加并恢复待处理'}
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
